import { formatDate } from '@angular/common'
import {
  AfterViewChecked,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import moment from 'moment'
import { Moment } from 'moment/moment'
import { finalize, firstValueFrom, forkJoin } from 'rxjs'
import { ClientInformationAttribute } from 'src/app/models/client-information/ClientInformationAttribute'
import { LogLevel } from '../../../common/log-level'
import { ClientDto } from '../../../models/client/client-dto'
import { AssessmentDto } from '../../../models/flip/assessment-dto'
import { AssessmentGroupDto } from '../../../models/flip/assessment-group-dto'
import { AssessmentItemDto } from '../../../models/flip/assessment-item-dto'
import { FlipItem } from '../../../models/flip/flip-item'
import { BannerService } from '../../../services/banner/banner.service'
import { ClientService } from '../../../services/client/client.service'
import { CommonService } from '../../../services/common/common.service'
import { FlipService } from '../../../services/flip/flip.service'
import { LogService } from '../../../services/logger/log.service'
import { PreviousRouteService } from '../../../services/previous-route/previous-route.service'
import { PresenterRole } from '../../../services/reddi/reddi-utils'
import { SpinnerService } from '../../../services/spinner/spinner.service'
import { AreYouSureModalComponent } from '../../complex/modals/are-you-sure-modal/are-you-sure-modal.component'
import { FlipAssessmentSideCardComponent } from '../../core/flip-assessment-side-card/flip-assessment-side-card.component'
import { PrimaryCardComponent } from '../../core/primary-card/primary-card.component'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-flip-assessment',
  templateUrl: './flip-assessment.component.html',
  styleUrls: ['./flip-assessment.component.scss'],
  standalone: false,
})
export class FlipAssessmentComponent implements OnInit, AfterViewChecked {
  @Input() clientId: string
  @Input() type: string = this.commonService.getFlipType()
  maxLength: number = 500

  _clientForm = new FormGroup({
    assessmentDate: new FormControl<Moment>(null, [
      Validators.required,
      this.dateValidator(),
    ]),
    textArea: new FormControl<string>('', [
      Validators.maxLength(this.maxLength),
    ]),
  })

  @ViewChild(FlipAssessmentSideCardComponent, { static: false })
  flipAssessmentSideCardComponent: FlipAssessmentSideCardComponent
  @ViewChild(PrimaryCardComponent, { static: false })
  primaryCardComponent: PrimaryCardComponent
  _flipAssessmentDataDto: AssessmentDto
  _currentFlipAssessmentGroup: AssessmentGroupDto
  _helpTipTitle = 'Help tip'
  _clientName: string
  _age: string
  _dateOfBirth: Date = new Date()
  _stillProcessing: boolean = true
  _ceilingMet: boolean = false
  _hideFlipAssessmentActionButton: boolean = false
  _actionsTaken: string[] = []
  _currItem: FlipItem
  _showClientDetails: boolean = false
  _scrollIndex: number = 0
  _dashboardUrl: string
  _isChildVisible: boolean = true
  //Client Attributes
  _clientInformationAttributes: ClientInformationAttribute[] =
    this.commonService.createEmptyAttributeList()
  _clientInformationTitle = 'Client Information'
  _clientInformationDisplayText = ''
  _clientInformationUrl = ''
  _clientInFocus: string = ''
  _clientEditEnabled: boolean = false //Perform check based on Feature Flag
  _totalReviews = 0
  _hasAccess: any = {
    slatHasAccess: false,
    flipHasAccess: false,
    reddiHasAccess: false,
  }
  private _clientDto: ClientDto
  private _basalMet: boolean = false
  private _basalCount: number = 0
  private _basalMax: number = 4
  private _items: FlipItem[]
  private _beforeBasalMetItems: FlipItem[]
  private _lastAssessmentDate: Date
  private _hasScrolled = false

  constructor(
    public flipService: FlipService,
    public router: Router,
    private logger: LogService,
    private clientService: ClientService,
    private dialog: MatDialog,
    protected previousRouteService: PreviousRouteService,
    private route: ActivatedRoute,
    public spinnerService: SpinnerService,
    public bannerService: BannerService,
    private readonly commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.spinnerService.show()

    this.clientId = this.route.snapshot.params['clientId']
    this._dashboardUrl = 'client-dashboard/' + this.clientId

    this.type = this.commonService.getFlipType()

    forkJoin([
      this.clientService.Details(this.clientId),
      this.flipService.GetAssessment(this.clientId, this.type),
      this.flipService.GetLastAssessment(this.clientId, this.type),
    ])
      .pipe(
        finalize(() => {
          this.spinnerService.hide()
          this._stillProcessing = false
        }),
      )
      .subscribe(
        ([clientDto, assessmentDto, assessmentDto2]) => {
          this._clientDto = clientDto
          this.setClientInformation(clientDto)
          this._clientName = this._clientDto.preferredFullName
          this._age = this._clientDto.age
          this._dateOfBirth = this._clientDto.dateOfBirth
          this._showClientDetails = true
          this._flipAssessmentDataDto = assessmentDto
          this._basalMax = this._flipAssessmentDataDto.basal
          this._flipAssessmentDataDto.assessmentDate =
            this._flipAssessmentDataDto.assessmentDate ?? moment().toDate()
          this._flipAssessmentDataDto.clientId = this.clientId
          const flipAssessmentAllItems =
            this._flipAssessmentDataDto.groups.flatMap((a) => a.items)
          this._beforeBasalMetItems = []
          this.extractItems(flipAssessmentAllItems)
          this._currItem = this.getInitialItem()
          this._currentFlipAssessmentGroup =
            this._flipAssessmentDataDto.groups.find(
              (a) => a.groupId == this._currItem.assessmentItem.groupId,
            )
          this._currItem.assessmentItem.selected = true
          this._scrollIndex = this._currItem.index
          const controls = this._clientForm.controls
          controls.assessmentDate.setValue(
            moment(this._flipAssessmentDataDto.assessmentDate),
          )

          const lastad = new Date(assessmentDto2.assessmentDate)
          if (this.isFirstAssessment(lastad)) {
            this._lastAssessmentDate = this._dateOfBirth
          } else {
            this._lastAssessmentDate = new Date(
              lastad.getFullYear(),
              lastad.getMonth(),
              lastad.getDate(),
            )
          }

          //tools has access check
          this.checkToolsAccess()
          this.retriveIndicators(clientDto)
        },
        (error) => {
          this.spinnerService.hide()
          console.log(error)
          throw error
        },
      )
  }

  ngAfterViewChecked() {
    if (this.flipAssessmentSideCardComponent && !this._hasScrolled) {
      this.flipAssessmentSideCardComponent.scrollToItem(
        this._currItem.index - 1,
      ) // scroll to the right place
      this._hasScrolled = true
    }
  }

  isFirstAssessment(assessmentDate: Date): boolean {
    return (
      assessmentDate.getFullYear() < new Date(this._dateOfBirth).getFullYear()
    )
  }

  extractItems(flipAssessmentAllItems: AssessmentItemDto[]) {
    this._items = []
    for (let count = 0; count < flipAssessmentAllItems.length; count++) {
      let item = flipAssessmentAllItems[count]

      let flipItem: FlipItem = {
        id: item.orderNumber,
        index: count,
        prevAnswer: item.score,
        currAnswer: null,
        assessmentItem: item,
        edited: false,
      }

      this._items.push(flipItem)
    }
  }

  getAssessmentErrorText(): string {
    const from = formatDate(this._lastAssessmentDate, 'dd/MM/yyyy', 'en')
    const to = formatDate(new Date(), 'dd/MM/yyyy', 'en')
    return 'Date should be between ' + from + ' and ' + to
  }

  navigateTo(target: string): void {
    this.commonService.navigateTo(target, this.clientId)
  }

  submit() {
    if (!this._clientForm.valid) {
      return
    }
    this.spinnerService.show()
    this._hideFlipAssessmentActionButton = true
    this._flipAssessmentDataDto.assessmentDate =
      this._clientForm.controls.assessmentDate.value.toDate()
    this._flipAssessmentDataDto.notes = this._clientForm.controls.textArea.value
    this._flipAssessmentDataDto.finishingSession = true
    this._flipAssessmentDataDto.presenterRole = PresenterRole.Professional
    this.flipService.Submit(this._flipAssessmentDataDto).subscribe({
      next: (response: any) => {
        this.logger.log(
          'Submit Assessment completed successfully',
          LogLevel.Debug,
          this._flipAssessmentDataDto,
        )
      },
      error: (error: any) => {
        this.spinnerService.hide()
        this.bannerService.showBanner(
          'Error creating FLI-P Assessment.',
          'error',
          5000,
        )
        this._hideFlipAssessmentActionButton = false
        throw error
      },
      complete: () => {
        this.spinnerService.hide()
        this.bannerService.showBanner(
          'FLI-P Assessment Created Successfully.',
          'success',
          5000,
        )
        this._clientForm.markAsPristine()
        this.clientService.clearClientCache()
        // go back to dashboard
        this.router.navigateByUrl(this._dashboardUrl, {
          state: { startAutoRefresh: true },
        })
      },
    })
  }

  async areYouSureDialog(): Promise<boolean> {
    const dr = this.dialog.open(AreYouSureModalComponent, {
      width: '605px',
      height: '316px',
      panelClass: 'are-you-sure-panel',
    })

    dr.afterClosed().subscribe((result) => {
      if (result) {
        // redirect to caller
        this.previousRouteService.goToPreviousUrl(this._dashboardUrl)
      }
    })

    return await firstValueFrom(dr.afterClosed())
  }

  showConfirmationDialog(): Promise<boolean> {
    return Promise.resolve(this.areYouSureDialog())
  }

  dateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value

      if (!value) {
        return null
      }

      if (this._lastAssessmentDate !== undefined) {
        const isBefore = moment(value).isBefore(this._lastAssessmentDate)
        if (isBefore) {
          return { inPast: true }
        }
      }

      // isAfter() will default to current date time and the field doesn't use time
      // so value = 1/1/2020 00:00:00 now = 1/1/2020 01:38:21
      return moment(value).isAfter() ? { inFuture: true } : null
    }
  }

  appendAction(response: boolean) {
    this._actionsTaken.push(response ? 'Mostly' : 'Rarely')
  }

  recordActivity(response: boolean) {
    // record the edited flag before moving to next item
    this._currItem.assessmentItem.edited = true
    this._currItem.edited = true

    this._currItem.currAnswer = response
    this._currItem.assessmentItem.score = response

    if (!this._basalMet) {
      // once basal is met we will
      // stop redo processing and instead
      // keep looking for ceilingMet
      this.redoAssessmentLogic()
      this.appendAction(response)
    } else {
      this._ceilingMet = this.isAssessmentComplete()
    }

    if (this._ceilingMet) {
      // all done
      // this.setLog('ceiling met')
      this.broadcastCeilingMet()
    } else if (!this._basalMet) {
      // this.setLog(
      //   'basal not met currItem ' + this._currItem.assessmentItem.itemNumber,
      // )
      // select new item
      this._currItem.assessmentItem.selected = false // old item deselected
      this._currItem = this.searchForBasalItem(this._currItem.index - 1)
      this._currItem.assessmentItem.selected = true // new item selected
      this._beforeBasalMetItems.push(this._currItem) // Add to stack
      // this.setLog(
      //   'basal not met newItem ' + this._currItem.assessmentItem.itemNumber,
      // )
    } else {
      // this.setLog('basal met')
      this._currItem.assessmentItem.selected = false // old item deselected
      this._currItem = this.getNextItem()
      this._currItem.assessmentItem.selected = true // new item selected
      // this.setLog('basal newItem ' + this._currItem.assessmentItem.itemNumber)
    }

    if (this.flipAssessmentSideCardComponent) {
      this.flipAssessmentSideCardComponent.scrollToItem(
        this._currItem.index - 1,
      ) // scroll to the right place
    }
    this._currentFlipAssessmentGroup = this._flipAssessmentDataDto.groups.find(
      (a) => a.groupId == this._currItem.assessmentItem.groupId,
    )
  }

  processAssessmentLogic(response: boolean, itemNumber: string) {
    // If we are still establishing basal
    if (!this._basalMet) {
      if (response) {
        this._basalCount++
        this._basalMet = this._basalCount >= this._basalMax
      } else {
        this._basalCount = 0
      }

      this.setLog(
        'itemNumber ' +
          itemNumber +
          ' basalCount ' +
          this._basalCount +
          ' basalMet ' +
          this._basalMet +
          ' response ' +
          response,
      )
    }
  }

  redoAssessmentLogic() {
    this._basalCount = 0
    this._basalMet = false
    this._ceilingMet = false
    this._actionsTaken = []

    for (const element of this._items) {
      let item = element
      this.processAssessmentLogic(
        item.currAnswer,
        item.assessmentItem.itemNumber,
      )
      this.appendAction(item.currAnswer)
    }

    if (this._basalMet) {
      // Remove edits from all _beforeBasalMetItems items
      this.resetEditForPreBasalItems()
      this._ceilingMet = this.isAssessmentComplete()
    }

    // this.setLog(
    //   'basal met ' +
    //     this._basalMet +
    //     ' ceiling met ' +
    //     this._ceilingMet +
    //     ' basal count ' +
    //     this._basalCount,
    // )
  }

  resetEditForPreBasalItems() {
    this._beforeBasalMetItems.forEach((item) => {
      item.assessmentItem.edited = false
      item.edited = false
    })
    this._beforeBasalMetItems = [] // clear
  }

  areAllQuestionsAnswered(): boolean {
    // Exit if we have reached the end of the channel
    const index = this._items.findIndex((a) => a.id == this._currItem.id)
    if (index == this._items.length - 1) {
      return true
    }

    // If all questions have been answered lets enable save
    return this._items.filter((a) => a.currAnswer == null).length == 0
  }

  isAssessmentComplete(): boolean {
    let rarelys = 0

    if (this.areAllQuestionsAnswered()) {
      return true
    }

    for (let i = this._items.length - 1; i >= 0; i--) {
      let item = this._items[i]

      if (item.currAnswer || (item.prevAnswer && item.currAnswer === null)) {
        rarelys = 0
      } else if (item.currAnswer === false) {
        rarelys += 1
      }

      if (rarelys >= 6) {
        return true
      }
    }

    return false
  }

  getInitialItem(): FlipItem {
    return this.searchForBasalItem(this._items.length - 1)
  }

  searchForBasalItem(startPos: number): FlipItem {
    for (let i = startPos; i >= 0; i--) {
      let item = this._items[i]
      if (item.prevAnswer) {
        // this.setLog('searchForBasalItem item ' + item.assessmentItem.itemNumber)
        return item
      }
    }

    this._basalMet = true
    // this.setLog('searchForBasalItem basal met')
    return this.getNextItem()
  }

  getNextItem(): FlipItem {
    let item: FlipItem

    for (const element of this._items) {
      item = element
      if (item.currAnswer == null && !item.prevAnswer) {
        // this.setLog('found next item' + item.assessmentItem.itemNumber)
        return item
      }
    }

    // this.setLog('default next item' + item.assessmentItem.itemNumber)
    return item
  }

  clear() {
    this._basalCount = 0
    this._basalMet = false
    this._ceilingMet = false

    for (const element of this._items) {
      element.currAnswer = null
    }

    this._actionsTaken = []
    this._beforeBasalMetItems = []

    this._currItem = this.getInitialItem()
  }

  getAssessmentText() {
    return this._ceilingMet ? 'Assessment has ended' : 'Assessment in progress'
  }

  broadcastCeilingMet() {
    this._items.forEach((item, index) => {
      item.assessmentItem.ceilingMet = true
    })
  }

  handleEditClicked(itemId: string) {
    const editItem = this._items.find((a) => a.assessmentItem.itemId == itemId)
    if (editItem) {
      this._currItem.assessmentItem.selected = false // Deselect old item
      this._currItem = editItem
      this._currItem.assessmentItem.selected = true // Select new item
      this.flashChild()
    }
  }

  flashChild() {
    if (this.primaryCardComponent) {
      this.primaryCardComponent.flashForASecond()
    }
  }

  setLog(message: string) {
    // console.log(message)
  }

  setClientInformation(client: ClientDto) {
    this._clientInFocus = client.contactId
    this._clientInformationDisplayText = this._clientEditEnabled
      ? 'Edit profile'
      : ''
    this._clientInformationUrl = this._clientEditEnabled
      ? '/edit-client/' + client.contactId
      : ''

    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Age',
      client.age,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'DOB',
      moment(client.dateOfBirth).format('Do MMMM, YYYY'),
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Gender',
      client.genderCodeName,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Case Manager',
      client.clientCaseManagerName,
    )
  }

  checkToolsAccess() {
    this._hasAccess.flipHasAccess = this.commonService.clientHasAccessToFlip(
      this._clientDto,
    )
    this._hasAccess.reddiHasAccess = this.commonService.clientHasAccessToReddi(
      this._clientDto,
    )
    this._hasAccess.slatHasAccess = this.commonService.clientHasAccessToSlat(
      this._clientDto,
    )
  }

  retriveIndicators(client: ClientDto) {
    this._totalReviews = this.commonService.returnReviewIndicator(client)
  }

  previousRoute() {
    const prevUrl = this.previousRouteService.getPreviousUrl()
    if (prevUrl) {
      this.previousRouteService.goToPreviousUrl(prevUrl)
    }
  }
}
